import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Flame } from 'lucide-react';
import { Card, cn, Notice, Spinner } from '@tcrd/ui';
import { Page } from '../../components/Page';
import { MapView } from '../../components/map/MapView';
import type { MapEngine } from '../../components/map/engine';
import { api } from '../../lib/api';

interface Demand {
  window: 'now' | 'all';
  weekday: string;
  hour: string;
  total: number;
  cells: Array<{ lat: number; lng: number; count: number }>;
}

/** Zonas con demanda: dónde suelen pedir viajes (mismo día y hora de las últimas 8 semanas, o en general). */
export function DemandPage() {
  const [period, setPeriod] = useState<'now' | 'all'>('now');
  const q = useQuery({ queryKey: ['demand', period], queryFn: () => api<Demand>('/drivers/me/demand', { query: { window: period } }), refetchInterval: 5 * 60_000 });
  const [engine, setEngine] = useState<MapEngine | null>(null);
  const cells = useMemo(() => q.data?.cells ?? [], [q.data]);

  useEffect(() => {
    if (!engine) return;
    const max = Math.max(1, ...cells.map((c) => c.count));
    engine.setHeat?.(cells.map((c) => ({ lat: c.lat, lng: c.lng, weight: c.count / max })));
    if (cells.length) engine.fit(cells.slice(0, 50), 60);
  }, [engine, cells]);

  return (
    <Page title="Zonas con demanda" back="/driver/more" onRefresh={() => q.refetch()}>
      <div className="flex flex-col gap-4">
        <div role="radiogroup" aria-label="Periodo" className="grid grid-cols-2 gap-2">
          {([
            ['now', 'A esta hora'],
            ['all', 'Todas las horas'],
          ] as const).map(([w, l]) => (
            <button key={w} role="radio" aria-checked={period === w} onClick={() => setPeriod(w)} className={cn('tap h-11 rounded-2xl border font-semibold', period === w ? 'bg-brand-black text-white border-brand-black dark:bg-white dark:text-brand-black' : 'border-[var(--border)] bg-[var(--surface)]')}>
              {l}
            </button>
          ))}
        </div>
        <MapView onEngine={setEngine} className="h-[55vh] -mx-4 sm:mx-0 sm:rounded-3xl" />
        {q.isLoading && <Spinner />}
        {q.data && (
          <Card className="p-4 flex items-start gap-3">
            <Flame className="size-6 text-brand-red shrink-0" aria-hidden />
            <p className="text-sm">
              {q.data.total
                ? period === 'now'
                  ? `${q.data.total} solicitudes los ${q.data.weekday} cerca de las ${q.data.hour} en las últimas 8 semanas. Las zonas más rojas tienen más demanda.`
                  : `${q.data.total} solicitudes en las últimas 8 semanas. Las zonas más rojas tienen más demanda.`
                : 'Todavía no hay suficientes solicitudes para mostrar zonas. Prueba con "Todas las horas".'}
            </p>
          </Card>
        )}
        <Notice>Las zonas se calculan con las solicitudes de tus clientes y las abiertas a todos los conductores, agrupadas por sector (sin direcciones exactas).</Notice>
      </div>
    </Page>
  );
}
